import Document from '@tiptap/extension-document';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Link from '@tiptap/extension-link';
import Text from '@tiptap/extension-text';
import Image from '@tiptap/extension-image';
import {
    EditorContent,
    Extension,
    mergeAttributes,
    ReactRenderer,
    useEditor,
} from '@tiptap/react';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import tippy from 'tippy.js';
import StarterKit from '@tiptap/starter-kit';
import PopoverButton from './PopoverButton.jsx';
import RichEditorBase from './RichEditorBase.jsx';
import Modal from '~/components/Modal';
import TextFieldIcon from '~/components/icons/TextFieldIcon';
import { ArchiveBoxIcon, EnvelopeIcon, LinkIcon } from '@heroicons/react/24/outline/index.js';
import UserCircleSingleIcon from '~/components/icons/UserCircleSingleIcon';
import { CalendarIcon, Cog6ToothIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import UserCheckValidateIcon from '~/components/icons/UserCheckValidateIcon';
import TextInputWithInlineLabel from '~/components/TextInputWithInlineLabel';
import MagnifyingGlassIcon from '~/components/icons/MagnifyingGlassIcon';
import ClipboardTextIcon from '~/components/icons/ClipboardTextIcon';
import GroupIcon from '~/components/icons/GroupIcon';
import InvoiceIcon from '~/components/icons/InvoiceIcon.jsx';
import GiftIcon from '~/components/icons/GiftIcon';

const categoryIcons = {
    'Form Fields': <TextFieldIcon />,
    Event: <CalendarIcon />,
    'Booked With': <UserCheckValidateIcon />,
    Client: <UserCircleSingleIcon />,
    'Form Links': <LinkIcon />,
    Other: <EnvelopeIcon />,
    Payment: <CreditCardIcon />,
    Waitlist: <ClipboardTextIcon />,
    Class: <GroupIcon />,
    Packages: <ArchiveBoxIcon />,
    'General': <Cog6ToothIcon />,
    'Gift Certificate': <GiftIcon />,
    Invoice: <InvoiceIcon />,
};

const CATEGORY_SEARCH = 'search';

const SchemaItem = ({ item, onClick, showDescription }) => {
    return (
        <div
            className="cursor-pointer p-4 bg-white hover:bg-gray-100"
            onClick={onClick}
        >
            {item.label}
            {!!showDescription && (
                <div className="text-gray-500 text-sm leading-none">
                    {item.category}
                </div>
            )}
        </div>
    );
};

const SearchResults = ({ schema, search, onClick }) => {
    const results = schema.filter(i => {
        return search && i.label.toLowerCase().indexOf(search) > -1;
    });

    return (
        <div className="divide-y">
            {!results?.length && (
                <div className="text-gray-500 p-4">No results</div>
            )}
            {results.map((item, idx) => (
                <SchemaItem
                    key={idx}
                    showDescription={true}
                    item={item}
                    onClick={() => onClick(item)}
                />
            ))}
        </div>
    );
};

const InsertMenuButton = ({ schema, doInsert, className }) => {
    const [isShowing, setIsShowing] = useState(false);
    const [search, setSearch] = useState('');

    const schemaByCategory = Object.groupBy(schema, s => s.category);
    const [selectedCategory, setSelectedCategory] = useState(
        Object.keys(schemaByCategory)[0],
    );

    return (
        <div
            className={
                className ? className : `flex items-end justify-end -mt-8 mb-1`
            }
        >
            <button
                type="button"
                className="text-sm btn-light-pill-sm"
                onClick={() => setIsShowing(true)}
            >
                + Add Field
            </button>
            <Modal
                isOpen={isShowing}
                onClose={() => setIsShowing(false)}
                panelClassName="!p-0 sm:!p-0 overflow-hidden w-full"
            >
                <div className="flex items-start">
                    <div className="flex-0.5 p-4 border-r">
                        <TextInputWithInlineLabel
                            value={search}
                            onChange={setSearch}
                            className="mb-2"
                            inputClassName={`pl-10`}
                            placeholder="Search"
                            onFocus={() => setSelectedCategory(CATEGORY_SEARCH)}
                            prefix={
                                <MagnifyingGlassIcon className="text-primary-700 w-4 h-4" />
                            }
                        />
                        {Object.keys(schemaByCategory).map(category => (
                            <div
                                key={category}
                                className={`flex items-center rounded-lg px-3 py-2 cursor-pointer ${category === selectedCategory ? 'text-primary-700 bg-gray-300' : 'text-gray-500'}`}
                                onClick={() => setSelectedCategory(category)}
                            >
                                <div className="w-6 h-6 mr-2">
                                    {categoryIcons[category]}
                                </div>
                                {category}
                            </div>
                        ))}
                    </div>
                    <div className="flex-1 divide-y max-h-[600px] overflow-scroll" key={selectedCategory}>
                        {selectedCategory === CATEGORY_SEARCH && (
                            <SearchResults
                                search={search}
                                schema={schema}
                                onClick={item => {
                                    doInsert(item.key, item.html);
                                    setIsShowing(false);
                                }}
                            />
                        )}
                        {schemaByCategory[selectedCategory]?.map(item => (
                            <SchemaItem
                                key={item.key}
                                item={item}
                                onClick={() => {
                                    doInsert(item.key, item.html);
                                    setIsShowing(false);
                                }}
                            />
                        ))}
                    </div>
                </div>
            </Modal>
            {/*<PopoverButton*/}
            {/*    btnContent={*/}
            {/*        <div className="text-sm btn-light-pill-sm">Insert</div>*/}
            {/*    }*/}
            {/*    panelClassName="!p-0 overflow-hidden"*/}
            {/*>*/}
            {/*    {({ close }) => (*/}
            {/*        <div className="divide-y">*/}
            {/*            {Object.keys(schemaByCategory).map(category => (*/}
            {/*                <>*/}
            {/*                    <div className="px-4 py-2 text-sm bg-gray-100 text-gray-500 uppercase">*/}
            {/*                        {category}*/}
            {/*                    </div>*/}
            {/*                    {schemaByCategory[category].map(item => (*/}
            {/*                        <button*/}
            {/*                            key={item.key}*/}
            {/*                            type="button"*/}
            {/*                            className="px-4 w-full hover:text-primary-700 text-left block py-2"*/}
            {/*                            onClick={() => {*/}
            {/*                                doInsert(item.key, item.html);*/}
            {/*                                close();*/}
            {/*                            }}*/}
            {/*                        >*/}
            {/*                            {item.label}*/}
            {/*                        </button>*/}
            {/*                    ))}*/}
            {/*                </>*/}
            {/*            ))}*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</PopoverButton>*/}
        </div>
    );
};

const suggestion = schema => ({
    items: ({ query }) => {
        return schema
            .filter(item =>
                item.label.toLowerCase().startsWith(query.toLowerCase()),
            )
            .slice(0, 5);
    },

    char: '{',

    render: () => {
        let reactRenderer;
        let popup;

        return {
            onStart: props => {
                if (!props.clientRect) {
                    return;
                }

                reactRenderer = new ReactRenderer(MentionList, {
                    props,
                    editor: props.editor,
                });

                popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    content: reactRenderer.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                });
            },

            onUpdate(props) {
                reactRenderer.updateProps(props);

                if (!props.clientRect) {
                    return;
                }

                popup[0].setProps({
                    getReferenceClientRect: props.clientRect,
                });
            },

            onKeyDown(props) {
                if (props.event.key === 'Escape') {
                    popup[0].hide();

                    return true;
                }

                return reactRenderer.ref?.onKeyDown(props);
            },

            onExit() {
                popup[0].destroy();
                reactRenderer.destroy();
            },
        };
    },
});

const MentionList = forwardRef((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = index => {
        const item = props.items[index];

        if (item) {
            console.log('inserting', item);
            props.command({ id: item.key });
        }
    };

    const upHandler = () => {
        setSelectedIndex(
            (selectedIndex + props.items.length - 1) % props.items.length,
        );
    };

    const downHandler = () => {
        setSelectedIndex((selectedIndex + 1) % props.items.length);
    };

    const enterHandler = () => {
        selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [props.items]);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (event.key === 'ArrowUp') {
                upHandler();
                return true;
            }

            if (event.key === 'ArrowDown') {
                downHandler();
                return true;
            }

            if (event.key === 'Enter') {
                enterHandler();
                return true;
            }

            return false;
        },
    }));

    return (
        <div className="tag-items">
            {props.items.length ? (
                props.items.map((item, index) => (
                    <button
                        className={`tag-item ${index === selectedIndex ? 'is-selected' : ''}`}
                        key={index}
                        onClick={() => selectItem(index)}
                    >
                        {item.label}
                    </button>
                ))
            ) : (
                <div className="item px-2">No result</div>
            )}
        </div>
    );
});

export default ({
    initialValue,
    onChange,
    schema,
    className,
    showToolbar,
    appendAfterInsert,
    placeholder,
    insertMenuButtonClassName,
    noLinking,
    showInsertInline,
}) => {
    const [editor, setEditor] = useState(null);
    const schemaRef = useRef(schema);

    useEffect(() => {
        schemaRef.current = schema;
    }, [schema]);

    const extensions = [
        Mention.configure({
            HTMLAttributes: {
                class: 'mention',
            },
            renderText({ options, node }) {
                const item = node.attrs.id;
                return `${options.suggestion.char}${item?.label || item}`;
            },
            renderHTML({ options, node }) {
                const key = node.attrs.id;
                const schema = schemaRef.current;
                let item = schema.filter(i => i.key === key)[0] || key;
                if (
                    item === key &&
                    typeof key?.startsWith === 'function' &&
                    key?.startsWith('question-')
                ) {
                    // It's a question we couldn't find in the schema! Possible error!
                    item = {
                        key: key,
                        label: `Deleted Question (${key.replace('question-', '')})`,
                        className: 'text-red-700 bg-red-100',
                    };
                }

                return [
                    'span',
                    mergeAttributes(
                        { 'data-tag-key': item.key, class: item.className },
                        options.HTMLAttributes,
                    ),
                    `\{${item?.label || item}\}`,
                ];
            },
            suggestion: suggestion(schema),
        }),
    ];

    return (
        <div>
            <InsertMenuButton
                schema={schema}
                className={(showInsertInline || insertMenuButtonClassName) ? `${showInsertInline ? 'absolute right-6 top-[26px] z-10' : ''} ${insertMenuButtonClassName}` : null}
                doInsert={(itemId, html) => {
                    if (html) {
                        return editor.chain().insertContent(html).focus().run();
                    } else {
                        const content = {
                            type: 'mention',
                            attrs: {
                                id: itemId,
                            },
                        };
                        editor
                            .chain()
                            .insertContent(content)
                            .insertContent(appendAfterInsert)
                            .focus()
                            .run();
                    }
                }}
            />
            <RichEditorBase
                className={className}
                initialValue={initialValue}
                onChange={onChange}
                extensions={extensions}
                setEditor={setEditor}
                showToolbar={showToolbar}
                placeholder={placeholder}
                noLinking={noLinking}
            />
        </div>
    );
};
